import React from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";
import { LayoutDashboard, Inbox, Users2, HandHeart, HeartHandshake, Users, Shield, FileCheck, MapPin, Mail } from "lucide-react";

export default function OpsLayout() {
  const { user, loading } = useAuth();
  const { caps, ready } = useCapabilities();
  if (loading) return <div className="p-8 text-sm text-slate-500">Loading Operations…</div>;
  if (!user) return <Navigate to="/login?next=/ops" replace />;
  // BTC3 Foundation completion · Gate 3 — capability-driven Ops
  // visibility.  Backend still authoritatively rejects each
  // Operations endpoint with `require_admin` / role-specific
  // predicates; this bit only hides the shell for unauthorized
  // clients so the fallback view renders.
  if (ready && !(caps.operations_access || caps.content_operations || caps.review_operations)) {
    return (
      <div data-testid="ops-forbidden" className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <Shield className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900">Operations is private.</h1>
          <p className="text-slate-500 text-sm mt-2">This surface is restricted to BTC staff.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden" data-testid="ops-shell">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-slate-900">BTC Operations</span>
            <span className="text-xs text-slate-500">/ {user.role}</span>
          </div>
          <div className="text-sm text-slate-600 truncate min-w-0" data-testid="ops-current-user">{user.email}</div>
        </div>
        <nav className="max-w-7xl mx-auto px-6 flex gap-5 text-sm overflow-x-auto" data-testid="ops-nav">
          <Item to="/ops" end icon={LayoutDashboard} tid="ops-nav-dashboard">Dashboard</Item>
          <Item to="/ops/onboarding" icon={Inbox} tid="ops-nav-onboarding">Onboarding</Item>
          <Item to="/ops/people" icon={Users2} tid="ops-nav-people">People</Item>
          <Item to="/ops/volunteers" icon={HandHeart} tid="ops-nav-volunteers">Volunteers</Item>
          <Item to="/ops/buddies" icon={HeartHandshake} tid="ops-nav-buddies">Buddies</Item>
          <Item to="/ops/organizations" icon={Users} tid="ops-nav-organizations">Organizations</Item>
          <Item to="/ops/staff" icon={Shield} tid="ops-nav-staff">Staff / Operators</Item>
          <Item to="/ops/content-governance" icon={FileCheck} tid="ops-nav-content-governance">Content Governance</Item>
          <Item to="/ops/geographic-coverage" icon={MapPin} tid="ops-nav-geographic-coverage">Geographic Coverage</Item>
          {user.role === "admin" && <Item to="/ops/email" icon={Mail} tid="ops-nav-email">Email</Item>}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        <Outlet />
      </main>
    </div>
  );
}

function Item({ to, end, icon: Icon, tid, children }) {
  return (
    <NavLink
      to={to} end={end} data-testid={tid}
      className={({ isActive }) =>
        `py-2 border-b-2 -mb-px inline-flex items-center gap-2 ${isActive ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`
      }
    >
      <Icon className="w-4 h-4" /> {children}
    </NavLink>
  );
}
